import { useEffect, useState } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import Loading from "~/components/Loading";
import { ManifoldNextGenAppHeader } from "~/components/ManifoldNextGenAppHeader";
import { ManifoldNextGenSideNav } from "~/components/ManifoldNextGenSideNav";
import { NextGenHeaderProvider } from "~/contexts/nextgen-header";
import { SideNavOverride, SideNavOverrideContext } from "~/contexts/nextgen-sidenav";
import { useManifoldNextGenFeature } from "~/hooks/useManifoldNextGenFeature";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { ROUTE_404 } from "~/router/constants";

const DARK_MODE_KEY = "nextgen-dark-mode";

export function ManifoldNextGenLayout() {
  const feature = useManifoldNextGenFeature();
  const [preferences, updatePreferences] = useUserPreferences();
  const [collapsed, setCollapsed] = useState(false);
  const [sideNavOverride, setSideNavOverride] = useState<SideNavOverride | null>(null);
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(() => localStorage.getItem(DARK_MODE_KEY) === "true");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem(DARK_MODE_KEY, String(isDark));
  }, [isDark]);

  if (feature.isLoading) {
    return <Loading />;
  }

  if (!feature.enabled || !preferences.optedIntoManifoldNextGen) {
    return <Navigate to={ROUTE_404} replace />;
  }

  const handleSwitchToClassic = () => {
    updatePreferences({ optedIntoManifoldNextGen: false });
    navigate("/");
  };

  return (
    <SideNavOverrideContext.Provider
      value={{ override: sideNavOverride, setOverride: setSideNavOverride }}
    >
      <NextGenHeaderProvider>
        <QueryParamProvider adapter={ReactRouter6Adapter} options={{ removeDefaultsFromUrl: true }}>
          <div
            data-theme="manifold-nextgen"
            className="flex h-screen bg-background text-foreground"
          >
            <ManifoldNextGenSideNav
              collapsed={sideNavOverride?.collapsed ?? collapsed}
              onCollapsedChange={setCollapsed}
              disableExpand={sideNavOverride?.disableExpand ?? false}
              isDark={isDark}
              onToggleDark={() => setIsDark((d) => !d)}
              onSwitchToClassic={handleSwitchToClassic}
            />

            <main className="flex min-w-0 flex-1 flex-col overflow-auto">
              <ManifoldNextGenAppHeader />
              <div className="flex-1 overflow-auto">
                <Outlet />
              </div>
            </main>
          </div>
        </QueryParamProvider>
      </NextGenHeaderProvider>
    </SideNavOverrideContext.Provider>
  );
}
